/**
 * Super-admin Pricing Management module. Every price on the platform is
 * configurable from this single page — nothing is hardcoded.
 *
 * Sections: Currency & Taxes · Patient pricing · Lab pricing · Recharge packs · Promotions
 * Data source: GET/PUT /api/admin/pricing.
 */
import React, { useEffect, useMemo, useState } from "react";
import { DashboardLayout } from "@/components/Layout";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { IndianRupee, Layers, Tag, ShieldCheck, Plus, Trash2, Loader2, Save, Star as StarIcon, Package as PackageIcon, Beaker, Users } from "lucide-react";

const emptyPack = () => ({ id: `pack-${Date.now()}`, amount: 100, bonus_credits: 0, label: "Custom", popular: false });
const emptyPlan = () => ({ id: `plan-${Date.now()}`, name: "New Plan", monthly_price: 0, annual_price: 0, reports_included: 100, per_report_overage: 5, features: [] });
const emptyPromo = () => ({ id: `promo-${Date.now()}`, code: "SAVE10", discount_type: "percent", value: 10, applies_to: "recharge", active: true, min_amount: 0, max_discount: 0, description: "" });

export default function PricingManagement() {
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/pricing")
      .then((r) => setPricing(r.data))
      .catch((e) => toast.error(apiError(e, "Could not load pricing")))
      .finally(() => setLoading(false));
  }, []);

  const symbol = pricing?.currency?.symbol || "₹";

  const save = async () => {
    if (!pricing) return;
    setSaving(true);
    try {
      const r = await api.put("/admin/pricing", pricing);
      setPricing(r.data);
      toast.success("Pricing saved — changes are live everywhere");
    } catch (e) { toast.error(apiError(e, "Save failed")); }
    finally { setSaving(false); }
  };

  const set = (path, value) => {
    setPricing((prev) => {
      const next = structuredClone(prev);
      const parts = path.split(".");
      let obj = next;
      for (let i = 0; i < parts.length - 1; i++) obj = obj[parts[i]] = obj[parts[i]] ?? {};
      obj[parts[parts.length - 1]] = value;
      return next;
    });
  };

  if (loading || !pricing) {
    return (
      <DashboardLayout role="super_admin">
        <div className="p-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-ink/40 mx-auto" /></div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role="super_admin">
      <div className="max-w-5xl">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-heading font-bold text-3xl text-ink flex items-center gap-2">
              <IndianRupee className="w-6 h-6 text-efgh-orange" /> Pricing Management
            </h1>
            <p className="text-ink/60 text-sm mt-1">Every price the platform charges — patients, labs, taxes, promos — is fully configurable here.</p>
          </div>
          <Button data-testid="save-pricing" onClick={save} disabled={saving} className="bg-ink hover:bg-ink/90 text-white h-11">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save all changes
          </Button>
        </div>

        {/* ============ Currency + Taxes ============ */}
        <Section title="Currency & Taxes" icon={ShieldCheck}>
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label>Currency code</Label>
              <Select value={pricing.currency?.code || "INR"} onValueChange={(v) => set("currency.code", v)}>
                <SelectTrigger data-testid="pr-currency-code"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="INR">INR — Indian Rupee</SelectItem>
                  <SelectItem value="USD">USD — US Dollar</SelectItem>
                  <SelectItem value="EUR">EUR — Euro</SelectItem>
                  <SelectItem value="AED">AED — UAE Dirham</SelectItem>
                  <SelectItem value="GBP">GBP — British Pound</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Currency symbol</Label>
              <Input data-testid="pr-currency-symbol" value={pricing.currency?.symbol || ""} onChange={(e) => set("currency.symbol", e.target.value)} />
            </div>
            <div>
              <Label>Tax label</Label>
              <Input data-testid="pr-tax-label" value={pricing.taxes?.tax_label || "GST"} onChange={(e) => set("taxes.tax_label", e.target.value)} />
            </div>
            <div>
              <Label>Tax rate %</Label>
              <Input type="number" step="0.5" min="0" data-testid="pr-tax-rate" value={pricing.taxes?.gst_percent ?? 0} onChange={(e) => set("taxes.gst_percent", parseFloat(e.target.value) || 0)} />
            </div>
            <div className="md:col-span-2">
              <label className="flex items-center gap-2 h-10 mt-6">
                <input type="checkbox" data-testid="pr-tax-included" checked={!!pricing.taxes?.gst_included_in_price} onChange={(e) => set("taxes.gst_included_in_price", e.target.checked)} className="w-4 h-4" />
                <span className="text-sm text-ink">Tax is already included in displayed prices</span>
              </label>
            </div>
          </div>
        </Section>

        {/* ============ Patient pricing ============ */}
        <Section title="Patient pricing" icon={Users}>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Price per Smart Report ({symbol})</Label>
              <Input type="number" step="1" min="0" data-testid="pr-patient-report" value={pricing.patient?.smart_report_price ?? 0} onChange={(e) => set("patient.smart_report_price", parseFloat(e.target.value) || 0)} />
              <p className="text-[11px] text-ink/50 mt-1">Debited from the patient&apos;s wallet each time a report is generated.</p>
            </div>
          </div>
        </Section>

        {/* ============ Lab pricing ============ */}
        <Section title="Laboratory pricing" icon={Beaker}>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Price per Smart Report conversion ({symbol})</Label>
              <Input type="number" step="1" min="0" data-testid="pr-lab-conversion" value={pricing.lab?.smart_report_conversion_price ?? 0} onChange={(e) => set("lab.smart_report_conversion_price", parseFloat(e.target.value) || 0)} />
              <p className="text-[11px] text-ink/50 mt-1">Charged to labs for every LIS/email-ingested report EFGH converts.</p>
            </div>
            <div>
              <Label>White-label setup fee ({symbol})</Label>
              <Input type="number" step="100" min="0" data-testid="pr-wl-setup" value={pricing.lab?.white_label_setup_fee ?? 0} onChange={(e) => set("lab.white_label_setup_fee", parseFloat(e.target.value) || 0)} />
            </div>
            <div>
              <Label>White-label monthly fee ({symbol})</Label>
              <Input type="number" step="50" min="0" data-testid="pr-wl-monthly" value={pricing.lab?.white_label_monthly_fee ?? 0} onChange={(e) => set("lab.white_label_monthly_fee", parseFloat(e.target.value) || 0)} />
            </div>
          </div>

          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] uppercase tracking-widest text-ink/50 font-heading font-semibold">Subscription plans</div>
              <Button size="sm" variant="outline" data-testid="add-plan" onClick={() => set("lab.subscription_plans", [...(pricing.lab?.subscription_plans || []), emptyPlan()])}>
                <Plus className="w-3.5 h-3.5 mr-1" /> Add plan
              </Button>
            </div>
            <div className="space-y-3">
              {(pricing.lab?.subscription_plans || []).map((plan, i) => (
                <PlanRow key={plan.id || i} plan={plan} symbol={symbol} onChange={(p) => {
                  const arr = [...(pricing.lab?.subscription_plans || [])]; arr[i] = p; set("lab.subscription_plans", arr);
                }} onRemove={() => {
                  const arr = [...(pricing.lab?.subscription_plans || [])]; arr.splice(i, 1); set("lab.subscription_plans", arr);
                }} testidBase={`plan-${i}`} />
              ))}
              {(pricing.lab?.subscription_plans || []).length === 0 && (
                <div className="p-6 border border-dashed border-border rounded-xl text-center text-sm text-ink/50">No subscription plans yet — click <b>Add plan</b>.</div>
              )}
            </div>
          </div>
        </Section>

        {/* ============ Platform commissions ============ */}
        <Section title="Platform commissions" icon={IndianRupee}>
          <p className="text-[11px] text-ink/50 -mt-1 mb-3">Default % EFGH earns on each booking. Individual labs can override these from <b>Empanelled labs → Lab profile</b>.</p>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Marketplace booking commission (%)</Label>
              <Input type="number" step="0.5" min="0" max="100" data-testid="pr-comm-marketplace" value={pricing.commissions?.marketplace_commission_percent ?? 15} onChange={(e) => set("commissions.marketplace_commission_percent", parseFloat(e.target.value) || 0)} />
              <p className="text-[11px] text-ink/50 mt-1">% of GMV kept by EFGH on marketplace test bookings.</p>
            </div>
            <div>
              <Label>Follow-up booking commission (%)</Label>
              <Input type="number" step="0.5" min="0" max="100" data-testid="pr-comm-followup" value={pricing.commissions?.followup_commission_percent ?? 10} onChange={(e) => set("commissions.followup_commission_percent", parseFloat(e.target.value) || 0)} />
              <p className="text-[11px] text-ink/50 mt-1">% of confirmed follow-up (Book-Now) booking amount kept by EFGH.</p>
            </div>
          </div>
        </Section>

        {/* ============ Wallet recharge packs ============ */}
        <Section title="Wallet recharge packs" icon={PackageIcon}>
          <p className="text-[11px] text-ink/50 -mt-1 mb-3">Preset top-up amounts with optional bonus credits. Patients see these as one-click chips in the top-up dialog.</p>
          <div className="grid md:grid-cols-2 gap-3">
            {(pricing.recharge_packs || []).map((p, i) => (
              <PackRow key={p.id || i} pack={p} symbol={symbol} onChange={(np) => {
                const arr = [...(pricing.recharge_packs || [])]; arr[i] = np; set("recharge_packs", arr);
              }} onRemove={() => {
                const arr = [...(pricing.recharge_packs || [])]; arr.splice(i, 1); set("recharge_packs", arr);
              }} testidBase={`pack-${i}`} />
            ))}
          </div>
          <Button size="sm" variant="outline" data-testid="add-pack" onClick={() => set("recharge_packs", [...(pricing.recharge_packs || []), emptyPack()])} className="mt-3">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add recharge pack
          </Button>
        </Section>

        {/* ============ Lab wallet top-up packs ============ */}
        <Section title="Lab wallet top-up packs" icon={PackageIcon}>
          <p className="text-[11px] text-ink/50 -mt-1 mb-3">Preset amounts labs can recharge their EFGH wallet with. Bonus is free credit added on top.</p>
          <div className="grid md:grid-cols-2 gap-3">
            {(pricing.lab_wallet_topup_packs || []).map((p, i) => (
              <Card key={p.id || i} className="p-4 rounded-xl border border-border" data-testid={`lw-pack-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <Input className="max-w-[180px]" value={p.label || ""} onChange={(e) => {
                    const arr = [...(pricing.lab_wallet_topup_packs || [])];
                    arr[i] = { ...arr[i], label: e.target.value };
                    set("lab_wallet_topup_packs", arr);
                  }} placeholder="Label" data-testid={`lw-pack-${i}-label`} />
                  <button type="button" onClick={() => {
                    const arr = [...(pricing.lab_wallet_topup_packs || [])];
                    arr.splice(i, 1);
                    set("lab_wallet_topup_packs", arr);
                  }} className="text-ink/40 hover:text-danger" data-testid={`lw-pack-${i}-remove`}><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label className="text-[11px]">Amount ({symbol})</Label>
                    <Input type="number" min="100" step="100" data-testid={`lw-pack-${i}-amount`} value={p.amount ?? 0} onChange={(e) => {
                      const arr = [...(pricing.lab_wallet_topup_packs || [])];
                      arr[i] = { ...arr[i], amount: parseFloat(e.target.value) || 0 };
                      set("lab_wallet_topup_packs", arr);
                    }} />
                  </div>
                  <div>
                    <Label className="text-[11px]">Bonus ({symbol})</Label>
                    <Input type="number" min="0" step="10" data-testid={`lw-pack-${i}-bonus`} value={p.bonus ?? 0} onChange={(e) => {
                      const arr = [...(pricing.lab_wallet_topup_packs || [])];
                      arr[i] = { ...arr[i], bonus: parseFloat(e.target.value) || 0 };
                      set("lab_wallet_topup_packs", arr);
                    }} />
                  </div>
                  <div className="flex items-end pb-1">
                    <label className="flex items-center gap-1.5 text-xs">
                      <input type="checkbox" checked={!!p.popular} onChange={(e) => {
                        const arr = [...(pricing.lab_wallet_topup_packs || [])];
                        arr[i] = { ...arr[i], popular: e.target.checked };
                        set("lab_wallet_topup_packs", arr);
                      }} data-testid={`lw-pack-${i}-popular`} /> Popular
                    </label>
                  </div>
                </div>
              </Card>
            ))}
          </div>
          <Button size="sm" variant="outline" data-testid="add-lw-pack" onClick={() => set("lab_wallet_topup_packs", [
            ...(pricing.lab_wallet_topup_packs || []),
            { id: `lw-${Date.now()}`, amount: 1000, bonus: 0, label: "Custom", popular: false },
          ])} className="mt-3">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add top-up pack
          </Button>
        </Section>

        {/* ============ Promotions ============ */}
        <Section title="Promotional pricing" icon={Tag}>
          <p className="text-[11px] text-ink/50 -mt-1 mb-3">Coupon codes with % or flat discounts. Toggle <b>active</b> to switch on/off instantly.</p>
          <div className="space-y-3">
            {(pricing.promotions || []).map((p, i) => (
              <PromoRow key={p.id || i} promo={p} symbol={symbol} onChange={(np) => {
                const arr = [...(pricing.promotions || [])]; arr[i] = np; set("promotions", arr);
              }} onRemove={() => {
                const arr = [...(pricing.promotions || [])]; arr.splice(i, 1); set("promotions", arr);
              }} testidBase={`promo-${i}`} />
            ))}
            {(pricing.promotions || []).length === 0 && (
              <div className="p-6 border border-dashed border-border rounded-xl text-center text-sm text-ink/50">No promotions active. Click <b>Add promo</b> to create one.</div>
            )}
          </div>
          <Button size="sm" variant="outline" data-testid="add-promo" onClick={() => set("promotions", [...(pricing.promotions || []), emptyPromo()])} className="mt-3">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add promo
          </Button>
        </Section>

        <div className="mt-8 flex justify-end">
          <Button data-testid="save-pricing-bottom" onClick={save} disabled={saving} className="bg-ink hover:bg-ink/90 text-white h-11">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save all changes
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <Card className="mt-6 p-6 rounded-2xl">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg bg-efgh-orange/10 text-efgh-orange grid place-items-center"><Icon className="w-4 h-4" /></div>
        <div className="font-heading font-semibold text-lg text-ink">{title}</div>
      </div>
      {children}
    </Card>
  );
}

function PackRow({ pack, symbol, onChange, onRemove, testidBase }) {
  const set = (k, v) => onChange({ ...pack, [k]: v });
  return (
    <Card className="p-4 rounded-xl border border-border" data-testid={testidBase}>
      <div className="flex items-center justify-between gap-2 mb-3">
        <Input className="max-w-[140px]" placeholder="Label" data-testid={`${testidBase}-label`} value={pack.label || ""} onChange={(e) => set("label", e.target.value)} />
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1 text-xs text-ink/60">
            <input type="checkbox" checked={!!pack.popular} onChange={(e) => set("popular", e.target.checked)} className="w-3.5 h-3.5" data-testid={`${testidBase}-popular`} /> popular
          </label>
          <button type="button" onClick={onRemove} data-testid={`${testidBase}-remove`} className="text-ink/40 hover:text-danger"><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[11px]">Amount ({symbol})</Label>
          <Input type="number" min="1" step="1" data-testid={`${testidBase}-amount`} value={pack.amount ?? 0} onChange={(e) => set("amount", parseFloat(e.target.value) || 0)} />
        </div>
        <div>
          <Label className="text-[11px]">Bonus credits</Label>
          <Input type="number" min="0" step="1" data-testid={`${testidBase}-bonus`} value={pack.bonus_credits ?? 0} onChange={(e) => set("bonus_credits", parseFloat(e.target.value) || 0)} />
        </div>
      </div>
    </Card>
  );
}

function PlanRow({ plan, symbol, onChange, onRemove, testidBase }) {
  const set = (k, v) => onChange({ ...plan, [k]: v });
  return (
    <Card className="p-4 rounded-xl border border-border" data-testid={testidBase}>
      <div className="flex items-center justify-between gap-2 mb-3">
        <Input className="max-w-[220px] font-semibold" placeholder="Plan name" data-testid={`${testidBase}-name`} value={plan.name || ""} onChange={(e) => set("name", e.target.value)} />
        <button type="button" onClick={onRemove} data-testid={`${testidBase}-remove`} className="text-ink/40 hover:text-danger"><Trash2 className="w-4 h-4" /></button>
      </div>
      <div className="grid md:grid-cols-4 gap-2">
        <div>
          <Label className="text-[11px]">Monthly ({symbol})</Label>
          <Input type="number" min="0" step="10" data-testid={`${testidBase}-monthly`} value={plan.monthly_price ?? 0} onChange={(e) => set("monthly_price", parseFloat(e.target.value) || 0)} />
        </div>
        <div>
          <Label className="text-[11px]">Annual ({symbol})</Label>
          <Input type="number" min="0" step="100" data-testid={`${testidBase}-annual`} value={plan.annual_price ?? 0} onChange={(e) => set("annual_price", parseFloat(e.target.value) || 0)} />
        </div>
        <div>
          <Label className="text-[11px]">Reports included</Label>
          <Input type="number" min="0" step="10" data-testid={`${testidBase}-included`} value={plan.reports_included ?? 0} onChange={(e) => set("reports_included", parseFloat(e.target.value) || 0)} />
        </div>
        <div>
          <Label className="text-[11px]">Overage / report ({symbol})</Label>
          <Input type="number" min="0" step="1" data-testid={`${testidBase}-overage`} value={plan.per_report_overage ?? 0} onChange={(e) => set("per_report_overage", parseFloat(e.target.value) || 0)} />
        </div>
      </div>
      <div className="mt-3 grid md:grid-cols-2 gap-2">
        <div>
          <Label className="text-[11px]">Wallet bonus on subscribe ({symbol})</Label>
          <Input type="number" min="0" step="10" data-testid={`${testidBase}-bonus`} value={plan.bonus_wallet_credit ?? 0} onChange={(e) => set("bonus_wallet_credit", parseFloat(e.target.value) || 0)} />
          <div className="text-[10px] text-ink/50 mt-1">Free lab-wallet credit granted on plan purchase (annual = 12×).</div>
        </div>
        <div>
          <Label className="text-[11px]">Features (comma-separated)</Label>
          <Input data-testid={`${testidBase}-features`} value={(plan.features || []).join(", ")} onChange={(e) => set("features", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} placeholder="Smart Reports, LIS, White-label" />
        </div>
      </div>
    </Card>
  );
}

function PromoRow({ promo, symbol, onChange, onRemove, testidBase }) {
  const set = (k, v) => onChange({ ...promo, [k]: v });
  return (
    <Card className="p-4 rounded-xl border border-border" data-testid={testidBase}>
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <label className="inline-flex items-center gap-1.5 text-xs">
            <input type="checkbox" checked={!!promo.active} onChange={(e) => set("active", e.target.checked)} className="w-4 h-4" data-testid={`${testidBase}-active`} />
            <span className={promo.active ? "text-efgh-green font-semibold" : "text-ink/50"}>{promo.active ? "Active" : "Inactive"}</span>
          </label>
        </div>
        <button type="button" onClick={onRemove} data-testid={`${testidBase}-remove`} className="text-ink/40 hover:text-danger"><Trash2 className="w-4 h-4" /></button>
      </div>
      <div className="grid md:grid-cols-4 gap-2">
        <div>
          <Label className="text-[11px]">Coupon code</Label>
          <Input data-testid={`${testidBase}-code`} value={promo.code || ""} onChange={(e) => set("code", e.target.value.toUpperCase())} className="font-mono uppercase" />
        </div>
        <div>
          <Label className="text-[11px]">Discount type</Label>
          <Select value={promo.discount_type || "percent"} onValueChange={(v) => set("discount_type", v)}>
            <SelectTrigger data-testid={`${testidBase}-type`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="percent">Percent %</SelectItem>
              <SelectItem value="flat">Flat {symbol}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px]">Value</Label>
          <Input type="number" min="0" step="1" data-testid={`${testidBase}-value`} value={promo.value ?? 0} onChange={(e) => set("value", parseFloat(e.target.value) || 0)} />
        </div>
        <div>
          <Label className="text-[11px]">Applies to</Label>
          <Select value={promo.applies_to || "recharge"} onValueChange={(v) => set("applies_to", v)}>
            <SelectTrigger data-testid={`${testidBase}-applies`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="recharge">Wallet recharge</SelectItem>
              <SelectItem value="smart_report">Smart Report</SelectItem>
              <SelectItem value="subscription">Lab subscription</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px]">Min amount ({symbol})</Label>
          <Input type="number" min="0" step="1" data-testid={`${testidBase}-min`} value={promo.min_amount ?? 0} onChange={(e) => set("min_amount", parseFloat(e.target.value) || 0)} />
        </div>
        <div>
          <Label className="text-[11px]">Max discount ({symbol})</Label>
          <Input type="number" min="0" step="1" data-testid={`${testidBase}-max`} value={promo.max_discount ?? 0} onChange={(e) => set("max_discount", parseFloat(e.target.value) || 0)} />
        </div>
        <div className="md:col-span-2">
          <Label className="text-[11px]">Description</Label>
          <Input data-testid={`${testidBase}-desc`} value={promo.description || ""} onChange={(e) => set("description", e.target.value)} placeholder="e.g. 50% off your first wallet recharge (max ₹200)" />
        </div>
      </div>
    </Card>
  );
}
